import React, {useEffect} from 'react';
import {AddLarge, SettingsAdjust} from '@carbon/icons-react';
import {
  Box,
  Button,
  Flex,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Text,
} from '@chakra-ui/react';
import {
  useContentPaneHeight,
  useIsFreeOrListBuilderCustomer,
} from '../../hooks/api/metadata';
import {useIsMyAccountsView} from './AccountViewContext';
import {useFilterStore} from './filterStore';
import {ToggleFilterControl} from './FilterPopoverComponents/ToggleFilterControl';
import {FilterItemList} from './FilterPopoverComponents/FilterItemList';
import {useFilterOptions} from './FilterPopoverComponents/useFilterOptions';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {ObjectId} from 'bson';
import {createAccountFilter} from '../../shared/filter/accountFilter';
import {SaveAsDynamicTag} from './SaveAsDynamicTag';

export const Filter = () => {
  const {
    excluded,
    setExcluded,
    disqualified,
    setDisqualified,
    filtersDifferFromDefault,
    numberOfActiveFiltersToDisplay,
    addEmptyFilterCondition,
    addEmptyFilterGroup,
    resetFilters,
    setFilterState,
  } = useFilterStore();

  const isMyAccountsView = useIsMyAccountsView();
  const isFreeOrListBuilderCustomer = useIsFreeOrListBuilderCustomer();
  const maxFilterPaneHeight = useContentPaneHeight();
  const activeFiltersCount = numberOfActiveFiltersToDisplay();
  const availableFilterOptions = useFilterOptions();

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const tagParam = searchParams.get('tag');
    if (tagParam) {
      // Set filter state with a tags filter from the URL
      setFilterState(
        createAccountFilter([
          {
            operator: 'in',
            field: 'tags',
            value: [new ObjectId(tagParam)],
          },
        ])
      );
      navigate({search: ''}, {replace: true});
    }
  }, [searchParams, navigate, setFilterState]);

  return (
    <Popover placement="bottom-end" isLazy>
      <PopoverTrigger>
        <Button
          variant="ghost"
          color="kgray.400"
          backgroundColor={activeFiltersCount ? 'kgray.100' : undefined}
          fontSize="14"
          fontWeight="500"
          leftIcon={
            <Box>
              <SettingsAdjust />
            </Box>
          }
        >
          Filter
          {activeFiltersCount ? (
            <Box
              backgroundColor="kblue.400"
              borderRadius="999px"
              width="21px"
              height="21px"
              color="white"
              display="flex"
              justifyContent="center"
              alignItems="center"
              fontSize="14px"
              fontWeight="500"
              ml={2}
            >
              {activeFiltersCount}
            </Box>
          ) : null}
        </Button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          fontSize={14}
          width="900px"
          maxHeight={maxFilterPaneHeight}
          overflowY="auto"
        >
          <PopoverHeader>
            <Flex justifyContent="space-between" alignItems="center" py={2}>
              <Text fontWeight={500} fontSize="lg">
                Show accounts where...
              </Text>

              {isMyAccountsView && !isFreeOrListBuilderCustomer && (
                <ToggleFilterControl
                  value={excluded ? 'excluded' : 'active'}
                  onChange={(value) => setExcluded(value === 'excluded')}
                  options={[
                    {value: 'active', label: 'Active'},
                    {value: 'excluded', label: 'Excluded'},
                  ]}
                />
              )}
              {!isMyAccountsView && !isFreeOrListBuilderCustomer && (
                <ToggleFilterControl
                  value={disqualified ? 'disqualified' : 'recommended'}
                  onChange={(value) =>
                    setDisqualified(value === 'disqualified')
                  }
                  options={[
                    {value: 'recommended', label: 'Recommended'},
                    {value: 'disqualified', label: 'Disqualified'},
                  ]}
                />
              )}
            </Flex>
          </PopoverHeader>
          <PopoverBody p={4} gap={4} display="flex" flexDirection="column">
            <FilterItemList availableOptions={availableFilterOptions} />
          </PopoverBody>
          <PopoverFooter borderTop="none">
            <Flex justifyContent="space-between" alignItems="center">
              <Flex gap={2} ml={-1}>
                <Button
                  size="sm"
                  p={1}
                  variant="ghost"
                  onClick={addEmptyFilterCondition}
                  leftIcon={<AddLarge />}
                >
                  Add filter
                </Button>
                <Button
                  p={1}
                  size="sm"
                  variant="ghost"
                  onClick={addEmptyFilterGroup}
                  leftIcon={<AddLarge />}
                >
                  Add filter group
                </Button>
              </Flex>
              <Button
                size="sm"
                p={1}
                variant="ghost"
                onClick={resetFilters}
                disabled={!filtersDifferFromDefault()}
              >
                Clear filters
              </Button>
            </Flex>
            {isMyAccountsView && <SaveAsDynamicTag />}
          </PopoverFooter>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
